import { type HTMLAttributes, type ReactNode, useState } from "react";
import { cn } from "@/shared/lib/cn";
import { avatarArt } from "@/shared/lib/avatarArt";
import { UserIcon } from "./icons";

export type AvatarTone =
  | "neutral"
  | "accent"
  | "image"
  | "video"
  | "voice"
  | "text"
  | "asset";

/** Account photo, seeded form, initials, or a neutral person icon. */
export interface AvatarProps {
  /** Initials or a short monogram. */
  label?: string;
  /** Account image; a failed or missing image falls back to the seeded form or initials. */
  src?: string;
  /** Stable account identity for a generated form when no photo is present. */
  seed?: string;
  /** Optional form family (0–5) for the avatar creator. */
  form?: number;
  className?: string;
  shape?: "person" | "record";
  size?: 24 | 30 | 32 | 40 | 56 | 96;
  tone?: AvatarTone;
}

export function Avatar({
  label,
  src,
  seed,
  form,
  className,
  shape = "person",
  size = 30,
  tone = "neutral",
}: AvatarProps) {
  const initial = label?.trim().slice(0, 2).toUpperCase();
  const source = src?.trim();
  const [failedSource, setFailedSource] = useState<string | null>(null);

  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size }}
      className={cn(
        "grid shrink-0 place-items-center overflow-hidden font-sans text-center text-[12.5px] font-semibold leading-none",
        shape === "person" ? "rounded-full" : "rounded-sm",
        avatarTone[tone],
        className,
      )}
    >
      {source && source !== failedSource
        ? <img src={source} alt="" width={size} height={size} className="size-full object-cover" onError={() => setFailedSource(source)} />
        : seed ? <svg viewBox="0 0 100 100" aria-hidden="true" style={{ width: "100%", height: "100%" }} className="bg-[var(--secondary-control-surface)] text-ink-soft" fill="currentColor">
          {avatarArt(seed, form).map((path, index) => <path key={index} {...path} fillRule="evenodd" />)}
        </svg> : initial || <UserIcon style={{ width: Math.round(size * .6), height: Math.round(size * .6) }} />}
    </span>
  );
}

const avatarTone: Record<AvatarTone, string> = {
  neutral: "bg-well text-ink-soft",
  accent: "bg-accent-subtle text-accent-text",
  image: "bg-media-image-subtle text-media-image-text",
  video: "bg-media-video-subtle text-media-video-text",
  voice: "bg-media-voice-subtle text-media-voice-text",
  text: "bg-media-text-subtle text-media-text-text",
  asset: "bg-media-asset-subtle text-media-asset-text",
};

export interface AvatarStackProps extends HTMLAttributes<HTMLDivElement> {
  /** Describes the represented people or records because individual avatars are decorative. */
  label: string;
  children: ReactNode;
  overflow?: number;
}

/** Overlapping avatars with one concise accessible name. */
export function AvatarStack({
  label,
  children,
  overflow = 0,
  className,
  ...props
}: AvatarStackProps) {
  return (
    <div
      {...props}
      role="img"
      aria-label={label}
      className={cn(
        "flex items-center [&>*+*]:-ms-2 [&>*]:ring-2 [&>*]:ring-paper",
        className,
      )}
    >
      {children}
      {overflow > 0 ? (
        <span
          aria-hidden="true"
          className="grid size-8 shrink-0 place-items-center rounded-full bg-canvas-soft text-[11px] font-semibold tabular-nums text-ink-soft"
        >
          +{overflow}
        </span>
      ) : null}
    </div>
  );
}
